// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { parseLocalDate } from '@/utils/formatters';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Video, Calendar, User, Package, Download, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import VideoMessageModal from '@/components/video/VideoMessageModal';
import OrderDetailsModal from '@/components/video/OrderDetailsModal';

interface VideoOrder {
  id: string;
  status: string;
  purchaser_name: string;
  purchaser_email: string;
  purchaser_phone?: string;
  player_name: string;
  team_name?: string;
  total_amount_cents: number;
  created_at: string;
  video_url?: string;
  notes?: string;
  vendor?: {
    business_name: string;
    phone?: string;
    email?: string;
  };
  event?: {
    name: string;
    start_date?: string;
    end_date?: string;
  };
  package?: {
    package_type: string;
  };
}

interface VideoOrdersProps {
  userRole?: 'parent' | 'customer' | 'athlete' | 'club_coach' | 'college_coach' | 'coach';
  showTitle?: boolean;
}

const VideoOrders: React.FC<VideoOrdersProps> = ({ 
  userRole = 'customer', 
  showTitle = true 
}) => {
  const { profile } = useAuth();
  const [orders, setOrders] = useState<VideoOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<VideoOrder | null>(null);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [orderDetailsModalOpen, setOrderDetailsModalOpen] = useState(false);

  useEffect(() => {
    fetchVideoOrders();
  }, [profile?.email]);

  const fetchVideoOrders = async () => {
    if (!profile?.email) {
      console.log('🔍 VideoOrders: No profile email available');
      setLoading(false);
      return;
    }

    console.log('🔍 VideoOrders: Fetching orders for email:', profile.email);

    try {
      // First let's see all orders to debug
      const { data: allOrders } = await supabase
        .from('video_orders')
        .select('purchaser_email, purchaser_name, player_name')
        .limit(5);
      
      console.log('🔍 VideoOrders: All orders in database:', allOrders);

      // Try to get orders by authenticated user's email from their profile
      const { data: orders, error } = await supabase
        .from('video_orders')
        .select(`
          id,
          status,
          purchaser_name,
          purchaser_email,
          purchaser_phone,
          player_name,
          team_name,
          total_amount_cents,
          created_at,
          video_url,
          notes,
          vendor_id,
          event_id,
          package_id
        `)
        .eq('purchaser_email', profile.email)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('🔍 VideoOrders: Supabase error:', error);
        toast.error('Failed to load video orders');
        return;
      }

      console.log('🔍 VideoOrders: Raw orders data:', orders);

      // Transform the data and fetch related data manually
      const transformedOrders = await Promise.all((orders || []).map(async (order: any) => {
        let vendorEmail = null;
        let vendorPhone = null;
        let vendorBusinessName = null;
        let eventName = null;
        let eventStartDate = null;
        let eventEndDate = null;
        let packageType = null;

        // Fetch vendor info if vendor_id exists
        if (order.vendor_id) {
          const { data: vendorData } = await supabase
            .from('video_vendor_profiles')
            .select('business_name, phone, user_id')
            .eq('id', order.vendor_id)
            .single();
          
          if (vendorData) {
            vendorBusinessName = vendorData.business_name;
            vendorPhone = vendorData.phone;
            
            // Fetch vendor email from profiles
            if (vendorData.user_id) {
              const { data: profileData } = await supabase
                .from('profiles')
                .select('email, phone')
                .eq('user_id', vendorData.user_id)
                .single();
              
              if (profileData) {
                vendorEmail = profileData.email;
                if (!vendorPhone) vendorPhone = profileData.phone;
              }
            }
          }
        }

        // Fetch event info if event_id exists
        if (order.event_id) {
          const { data: eventData } = await supabase
            .from('events')
            .select('name, start_date, end_date')
            .eq('id', order.event_id)
            .single();
          
          if (eventData) {
            eventName = eventData.name;
            eventStartDate = eventData.start_date;
            eventEndDate = eventData.end_date;
          }
        }

        // Fetch package info if package_id exists
        if (order.package_id) {
          const { data: packageData } = await supabase
            .from('video_packages')
            .select('package_type')
            .eq('id', order.package_id)
            .single();
          
          if (packageData) {
            packageType = packageData.package_type;
          }
        }

        return {
          ...order,
          vendor: {
            business_name: vendorBusinessName,
            email: vendorEmail,
            phone: vendorPhone,
            user_id: order.vendor_id
          },
          event: eventName ? {
            name: eventName,
            start_date: eventStartDate,
            end_date: eventEndDate
          } : null,
          package: packageType ? {
            package_type: packageType
          } : null,
        };
      }));

      setOrders(transformedOrders);
    } catch (error) {
      console.error('Error fetching video orders:', error);
      toast.error('Failed to load video orders');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (cents: number) => {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  };

  const formatDate = (dateString: string) => {
    return parseLocalDate(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'pending': { variant: 'secondary' as const, color: 'bg-yellow-100 text-yellow-800' },
      'paid': { variant: 'default' as const, color: 'bg-green-100 text-green-800' },
      'processing': { variant: 'secondary' as const, color: 'bg-blue-100 text-blue-800' },
      'completed': { variant: 'default' as const, color: 'bg-green-100 text-green-800' },
      'cancelled': { variant: 'destructive' as const, color: 'bg-red-100 text-red-800' },
      'revision_requested': { variant: 'secondary' as const, color: 'bg-orange-100 text-orange-800' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    
    return (
      <Badge variant={config.variant} className={config.color}>
        {status === 'revision_requested' ? 'Revision Requested' : status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const handleViewMessages = (order: VideoOrder) => {
    setSelectedOrder(order);
    setMessageModalOpen(true);
  };

  const handleViewVideo = (order: VideoOrder) => {
    if (order.video_url) {
      window.open(order.video_url, '_blank');
    } else {
      toast.error('Video URL not available');
    }
  };

  const handleOrderClick = (order: VideoOrder) => {
    setSelectedOrder(order);
    setOrderDetailsModalOpen(true);
  };

  const handleOrderUpdate = () => {
    fetchVideoOrders(); // Refresh the orders list
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          {showTitle && (
            <CardTitle className="flex items-center gap-2">
              <Video className="h-5 w-5" />
              Video Orders
            </CardTitle>
          )}
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            <span className="ml-2">Loading video orders...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (orders.length === 0) {
    return (
      <Card>
        <CardHeader>
          {showTitle && (
            <>
              <CardTitle className="flex items-center gap-2">
                <Video className="h-5 w-5" />
                Video Orders
              </CardTitle>
              <CardDescription>
                Track your video recording service orders
              </CardDescription>
            </>
          )}
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Video className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Video Orders</h3>
            <p className="text-gray-600">
              You haven't placed any video orders yet. Browse available video services at upcoming events.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        {showTitle && (
          <>
            <CardTitle className="flex items-center gap-2">
              <Video className="h-5 w-5" />
              Video Orders
            </CardTitle>
            <CardDescription>
              Track your video recording service orders
            </CardDescription>
          </>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {orders.map((order) => (
            <div
              key={order.id}
              className="border rounded-lg p-4 space-y-3 hover:bg-gray-50 transition-colors cursor-pointer"
              onClick={() => handleOrderClick(order)}
            >
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold">{order.vendor?.business_name}</h4>
                    {getStatusBadge(order.status)}
                  </div>
                  <p className="text-sm text-gray-600">
                    Order #{order.id.slice(0, 8)}...
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-semibold">{formatPrice(order.total_amount_cents)}</p>
                  <p className="text-sm text-gray-500">
                    {formatDate(order.created_at)}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                   <div>
                     <p className="font-medium">Event</p>
                     <p className="text-gray-600">{order.event?.name}</p>
                     {order.event?.start_date && (
                       <p className="text-gray-500">{formatDate(order.event.start_date)}</p>
                     )}
                   </div>
                </div>

                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="font-medium">Player</p>
                    <p className="text-gray-600">{order.player_name}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="font-medium">Package</p>
                    <p className="text-gray-600 capitalize">{order.package?.package_type}</p>
                  </div>
                </div>
              </div>

              {/* Show revision notes for parents if they exist */}
              {order.status === 'revision_requested' && order.notes && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <h5 className="font-medium text-orange-800 mb-1">Revision Request Details:</h5>
                  <p className="text-sm text-orange-700">{order.notes}</p>
                  <p className="text-xs text-orange-600 mt-1">Your revision request has been sent to the vendor.</p>
                </div>
              )}

              
              <div className="pt-2 border-t flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex-1"
                  onClick={() => handleViewMessages(order)}
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Messages
                </Button>
                
                {order.status === 'completed' && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => handleViewVideo(order)}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    View Video
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>

      {/* Video Message Modal */}
      <VideoMessageModal
        open={messageModalOpen}
        onOpenChange={setMessageModalOpen}
        order={selectedOrder ? {
          id: selectedOrder.id,
          purchaser_name: selectedOrder.purchaser_name,
          purchaser_email: selectedOrder.purchaser_email,
          purchaser_phone: selectedOrder.purchaser_phone || '',
          player_name: selectedOrder.player_name,
          team_name: selectedOrder.team_name,
          status: selectedOrder.status,
          event_name: selectedOrder.event?.name,
          package_type: selectedOrder.package?.package_type,
          vendor_business_name: selectedOrder.vendor?.business_name,
          vendor_phone: selectedOrder.vendor?.phone,
          vendor_email: selectedOrder.vendor?.email
        } : null}
        userType="customer"
      />

      {/* Order Details Modal for Parents */}
      <OrderDetailsModal
        open={orderDetailsModalOpen}
        onOpenChange={setOrderDetailsModalOpen}
        order={selectedOrder ? {
          id: selectedOrder.id,
          purchaser_name: selectedOrder.purchaser_name,
          purchaser_email: selectedOrder.purchaser_email,
          purchaser_phone: selectedOrder.purchaser_phone || '',
          player_name: selectedOrder.player_name,
          team_name: selectedOrder.team_name,
          status: selectedOrder.status,
          total_amount_cents: selectedOrder.total_amount_cents,
          created_at: selectedOrder.created_at,
          updated_at: new Date().toISOString(),
          video_url: selectedOrder.video_url,
          notes: selectedOrder.notes,
          vendor: selectedOrder.vendor,
          event: (selectedOrder.event && selectedOrder.event.start_date) ? {
            name: selectedOrder.event.name,
            start_date: selectedOrder.event.start_date
          } : undefined,
          package: selectedOrder.package ? {
            package_type: selectedOrder.package.package_type,
            price_cents: selectedOrder.total_amount_cents,
            description: selectedOrder.package.package_type
          } : undefined
        } : null}
        onOrderUpdate={handleOrderUpdate}
      />
    </Card>
  );
};

export default VideoOrders;